import { Injectable } from '@angular/core';
// import { CustomerInvoice } from "./invoice";
import { InvoiceItem } from './invoiceItem';
import { GatewayAPIService } from '../../core/gateway-api.service';
import { Page } from '../../components/ui/filterable-table/tableViewAbstractComponent';
import { Invoice } from './invoice';
import { PayableInvoice } from './payableInvoice';
import { InvoiceSettings } from './invoiceSettings';

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  options = { authenticate: true, setRoot: true, updateAuthHeader: false };

  constructor(private api: GatewayAPIService) {}

  public searchCatalogItems(
    merchantId: string,
    query: string,
    pageNum: number,
    pageSize: number,
    columns: string[],
  ): Promise<InvoiceItem[]> {
    return this.api
      .apiCallSane(
        '/search/catalog-items',
        'POST',
        this.api.getDefaultHeaders(),
        null,
        {
          merchantId: merchantId,
          query: query,
          pageNum: pageNum,
          pageSize: pageSize,
          columns: columns,
        },
        this.options,
      )
      .then((response: InvoiceItem[]) => response);
  }

  public getInvoices(
    merchantId: string,
    pageNum: number,
    pageSize: number,
    query: string,
  ): Promise<Page<Invoice>> {
    return this.api
      .apiCallSane(
        '/invoices',
        'GET',
        this.api.getDefaultHeaders(),
        null,
        {
          merchantId: merchantId,
          pageNum: pageNum,
          pageSize: pageSize,
          query: query,
        },
        this.options,
      )
      .then((response: Partial<Page<Invoice>>) => new Page<Invoice>(response));
  }

  public getPayableInvoice(invoiceId: string): Promise<PayableInvoice> {
    return this.api.apiCallSane(
      '/invoice/payable-invoice',
      'GET',
      this.api.getDefaultHeaders(),
      { invoiceId: invoiceId },
      null,
      this.options,
    );
  }

  public postPayableInvoice(
    invoiceId: string,
    amountPaid: number,
    remainingPayments: number,
    agreementId: string,
  ): Promise<boolean> {
    return this.api.apiCallSane(
      '/invoice/payable-invoice',
      'POST',
      this.api.getDefaultHeaders(),
      null,
      {
        invoiceId: invoiceId,
        amountPaid: amountPaid,
        remainingPayments: remainingPayments,
        agreementId: agreementId,
      },
      this.options,
    );
  }

  public getInvoice(invoiceId: string): Promise<Invoice> {
    return this.api.apiCallSane(
      '/invoice',
      'GET',
      this.api.getDefaultHeaders(),
      { invoiceId },
      null,
      this.options,
    );
  }

  public updateInvoice(invoice: Invoice): Promise<boolean> {
    return this.api.apiCallSane(
      '/invoice',
      'PUT',
      this.api.getDefaultHeaders(),
      null,
      invoice,
      this.options,
    );
  }

  public createInvoice(
    merchantId: string,
    invoiceSettings: InvoiceSettings,
  ): Promise<string> {
    return this.api.apiCallSane(
      '/invoice',
      'POST',
      this.api.getDefaultHeaders(),
      null,
      { merchantId: merchantId, invoiceSettings: invoiceSettings },
      this.options,
    );
  }

  public deleteInvoice(invoiceId: string): Promise<boolean> {
    return this.api.apiCallSane(
      '/invoice',
      'DELETE',
      this.api.getDefaultHeaders(),
      null,
      { invoiceId },
      this.options,
    );
  }

  public sendReminder(
    invoiceId: string,
    merchantId: string,
    customerId: string,
    customerEmail: string,
  ): Promise<boolean> {
    return this.api.apiCallSane(
      '/invoice/reminder',
      'POST',
      this.api.getDefaultHeaders(),
      null,
      {
        invoiceId: invoiceId,
        merchantId: merchantId,
        customerId: customerId,
        customerEmail: customerEmail,
      },
      this.options,
    );
  }

  public pauseReminders(
    invoiceId: string,
    pauseRemindersUntil: Date,
  ): Promise<boolean> {
    return this.api.apiCallSane(
      '/invoice/pause',
      'PUT',
      this.api.getDefaultHeaders(),
      null,
      { invoiceId: invoiceId, pauseRemindersUntil: pauseRemindersUntil },
      this.options,
    );
  }

  public getPaged(
    merchantId: string,
    query: string,
    pageNum: number,
    pageSize: number,
    sortColumn: string,
    sortDirection: string,
    invoiceStatus: string,
  ): Promise<Page<Invoice>> {
    return this.api
      .apiCallSane(
        '/invoices',
        'GET',
        this.api.getDefaultHeaders(),
        {
          merchantId: merchantId,
          query: query,
          pageNum: pageNum,
          pageSize: pageSize,
          invoiceStatus: invoiceStatus,
          sortColumn: sortColumn,
          sortDirection: sortDirection,
        },
        null,
        this.options,
      )
      .then((response: Partial<Page<Invoice>>) => {
        return new Page<Invoice>(response);
      });
  }

  public getInvoiceSettings(merchantId: string): Promise<InvoiceSettings> {
    return this.api.apiCallSane(
      '/invoice/settings',
      'GET',
      this.api.getDefaultHeaders(),
      { merchantId: merchantId },
      null,
      this.options,
    );
  }

  public createInvoiceSettings(
    invoiceSettings: InvoiceSettings,
  ): Promise<InvoiceSettings> {
    return this.api
      .apiCallSane(
        '/invoice/settings',
        'POST',
        this.api.getDefaultHeaders(),
        null,
        invoiceSettings,
        this.options,
      )
      .then((response: InvoiceSettings) => response);
  }

  public putInvoiceSettings(
    invoiceSettings: InvoiceSettings,
  ): Promise<boolean> {
    return this.api.apiCallSane(
      '/invoice/settings',
      'PUT',
      this.api.getDefaultHeaders(),
      null,
      invoiceSettings,
      this.options,
    );
  }

  public getInvoiceMerchantById(merchantId: string): Promise<any> {
    return this.api.apiCallSane(
      '/invoice/merchant',
      'GET',
      this.api.getDefaultHeaders(),
      { id: merchantId },
      null,
      this.options,
    );
  }

  public getInvoiceIdForExternalInvoice(merchantName: string, invoiceNumber: string, entityType: string, email: string): Promise<string> {
    const url = "/" + entityType + "/invoice-id";
    return this.api.apiCallSane(url, 'POST', this.api.getDefaultHeaders(), null, { merchantName: merchantName, invoiceNumber: invoiceNumber, email: email, entityType: entityType }, this.options);
  }
}
